import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as customersAPI from './customersAPI';

const initialState = {
  customers: [],
  currentCustomer: null,
  total: 0,
  page: 1,
  totalPages: 0,
  limit: 20,
  status: 'idle', // idle | loading | succeeded | failed
  error: null,
};

// Async thunks
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async ({ page = 1, limit = 20, filters = {} }, { rejectWithValue }) => {
    try {
      return await customersAPI.fetchCustomers(page, limit, filters);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCustomer = createAsyncThunk(
  'customers/fetchCustomer',
  async (id, { rejectWithValue }) => {
    try {
      return await customersAPI.fetchCustomer(id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (customerData, { rejectWithValue }) => {
    try {
      return await customersAPI.createCustomer(customerData);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await customersAPI.updateCustomer(id, data);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id, { rejectWithValue }) => {
    try {
      await customersAPI.deleteCustomer(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const customersSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearCurrentCustomer(state) {
      state.currentCustomer = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch customers (list with pagination)
      .addCase(fetchCustomers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.customers = action.payload.customers;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.limit = action.payload.limit;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch customers';
      })
      
      // Fetch single customer
      .addCase(fetchCustomer.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchCustomer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentCustomer = action.payload;
      })
      .addCase(fetchCustomer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch customer';
      })
      
      // Create customer
      .addCase(createCustomer.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.customers.unshift(action.payload);
        state.total += 1;
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to create customer';
      })
      
      // Update customer
      .addCase(updateCustomer.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.customers.findIndex(c => c._id === action.payload._id);
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
        if (state.currentCustomer?._id === action.payload._id) {
          state.currentCustomer = action.payload;
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to update customer';
      })
      
      // Delete customer
      .addCase(deleteCustomer.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.customers = state.customers.filter(c => c._id !== action.payload);
        state.total -= 1;
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to delete customer';
      });
  },
});

export const { clearError, clearCurrentCustomer } = customersSlice.actions;
export default customersSlice.reducer;
